/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { jsx, cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import Pressable from '@atlaskit/primitives/pressable';
import Spinner from '@atlaskit/spinner/spinner';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';

import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon.ts';
import { ContentTreeSearchBase } from '@confluence/content-tree-search/entry-points/ContentTreeSearchBase.ts';

import { useContentTitles } from './hooks/useContentTitles.ts';
import { MoveIcon } from './img/MoveIcon.tsx';

const styles = cssMap({
	movePopupContent: {
		padding: token('space.150'),
		width: '280px',
	},
	fallbackSectionLabel: {
		padding: token('space.050'),
	},
	fallbackParentRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
		padding: token('space.100'),
		width: '100%',
		backgroundColor: 'transparent',
		transition: token('motion.listitem.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.listitem.pressed'),
		},
		borderRadius: token('radius.large'),
	},
	fallbackParentTitle: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		minWidth: '0%',
		flex: 1,
		textAlign: 'start',
	},
	fallbackParentIcon: {
		flexShrink: 0,
	},
	spinnerContainer: {
		display: 'flex',
		justifyContent: 'center',
		padding: token('space.150'),
	},
});

const i18n = defineMessages({
	moveButton: {
		id: 'space-curation.review-content-suggestions-modal.move-button',
		defaultMessage: 'Move',
		description: 'Label for the move button on a suggestion item row',
	},
	moveToTitle: {
		id: 'space-curation.review-content-suggestions-modal.move-to-title',
		defaultMessage: 'Move to',
		description: 'Title shown at the top of the move popup',
	},
	suggested: {
		id: 'space-curation.review-content-suggestions-modal.move-suggested',
		defaultMessage: 'Suggested',
		description:
			'Section label shown above the list of suggested fallback parent locations in the move popup',
	},
	moveSuggestionTooltip: {
		id: 'space-curation.review-content-suggestions-modal.move-suggestion-tooltip',
		defaultMessage: 'Move suggestion to another parent',
		description: 'Tool tip for moving a suggestion',
	},
});

type FallbackParentRowProps = {
	parentId: number;
	meta: { type: string; title?: string } | undefined;
	title: string | undefined;
	onMoveItemClick: (parentId: number) => void;
};

const FallbackParentRow = ({ parentId, meta, title, onMoveItemClick }: FallbackParentRowProps) => {
	const handleClick = useCallback(() => {
		onMoveItemClick(parentId);
	}, [onMoveItemClick, parentId]);

	return (
		<Pressable
			key={parentId}
			xcss={styles.fallbackParentRow}
			onClick={handleClick}
			testId={`suggestion-move-fallback-parent-${parentId}`}
		>
			<Inline xcss={styles.fallbackParentIcon} alignBlock="center">
				<ContentIcon type={meta?.type ?? 'page'} />
			</Inline>
			<Box xcss={styles.fallbackParentTitle}>
				<Text>{meta?.title ?? title}</Text>
			</Box>
		</Pressable>
	);
};

export type SuggestionMovePopupProps = {
	suggestionId: string;
	spaceKey?: string;
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	onMoveSuggestion: (suggestionId: string, newParentId: number) => void;
	fallbackParents?: number[];
	currentParentId?: number;
};

export const SuggestionMovePopup = ({
	suggestionId,
	spaceKey,
	isOpen,
	onOpenChange,
	onMoveSuggestion,
	fallbackParents,
	currentParentId,
}: SuggestionMovePopupProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const filteredFallbackParents = useMemo(
		() => (fallbackParents ?? []).filter((id) => id !== currentParentId),
		[fallbackParents, currentParentId],
	);

	const fallbackParentIdStrings = useMemo(
		() => filteredFallbackParents.map(String),
		[filteredFallbackParents],
	);

	const { titlesByContentId, contentMetaById, isLoading } = useContentTitles(
		isOpen ? fallbackParentIdStrings : [],
	);

	const handleMoveItemClick = useCallback(
		(idOrAri: string | number) => {
			const str = String(idOrAri);
			const rawId = str.includes('/') ? (str.split('/').pop() ?? str) : str;
			const numId = Number(rawId);
			if (!rawId || isNaN(numId)) return;
			onMoveSuggestion(suggestionId, numId);
			onOpenChange(false);
		},
		[onMoveSuggestion, onOpenChange, suggestionId],
	);

	const renderFallbackParentList = () => {
		if (isLoading) {
			return (
				<Box xcss={styles.spinnerContainer}>
					<Spinner size="small" />
				</Box>
			);
		}
		const accessibleParents = filteredFallbackParents.filter((parentId) =>
			contentMetaById.has(String(parentId)),
		);

		if (accessibleParents.length === 0) {
			return null;
		}

		return (
			<Stack>
				<Box xcss={styles.fallbackSectionLabel}>
					<Text size="small" color="color.text.subtlest" weight="semibold">
						{formatMessage(i18n.suggested)}
					</Text>
				</Box>
				{accessibleParents.map((parentId) => (
					<FallbackParentRow
						key={parentId}
						parentId={parentId}
						meta={contentMetaById.get(String(parentId))}
						title={titlesByContentId.get(String(parentId))}
						onMoveItemClick={handleMoveItemClick}
					/>
				))}
			</Stack>
		);
	};

	const fallbackParentRows =
		isOpen && filteredFallbackParents.length > 0 ? renderFallbackParentList() : null;

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => onOpenChange(false)}
			placement="bottom-start"
			zIndex={800}
			content={() => (
				<Box xcss={styles.movePopupContent}>
					<Text weight="bold">{formatMessage(i18n.moveToTitle)}</Text>
					<ContentTreeSearchBase
						spaceKey={spaceKey ?? ''}
						onItemClick={handleMoveItemClick}
						contentId=""
						includeFolders
					>
						{fallbackParentRows}
					</ContentTreeSearchBase>
				</Box>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					appearance="subtle"
					spacing="compact"
					isSelected={isOpen}
					icon={() => <MoveIcon />}
					label={formatMessage(i18n.moveButton)}
					onClick={() => onOpenChange(!isOpen)}
					testId={`review-content-suggestion-move-button-${suggestionId}`}
					isTooltipDisabled={false}
					tooltip={{ content: formatMessage(i18n.moveSuggestionTooltip) }}
				/>
			)}
		/>
	);
};
